import { useCallback } from 'react';
import { useChatStore } from '@/store/useChatStore';
import { useStudioStore } from '@/store/useStudioStore';
import { socketActions } from '@/hooks/studio/useWebSocket';
import { nanoid } from 'nanoid';
import { rememberLegacyBuildStart } from '@/lib/generationTiming';

/**
 * useChatService - 对话业务 Hook
 * 职责：提供对话相关的核心业务逻辑方法，包括发送指令、中断任务等。
 */
export function useChatService() {
  const { appendMessage, setStatus, setIsGenerating, clearMessages } = useChatStore();
  const {
    currentProjectId: projectId,
    sessionId,
    setIsBuilding,
    setPendingCommand,
  } = useStudioStore();

  /**
   * 发送指令：与 AI 进行交互的主入口
   * @param command 用户输入的文本或指令
   * @param options 发送选项，如 { hidden: true } 则不回显消息
   */
  const sendCommand = useCallback(
    async (command: string, options: { hidden?: boolean } = {}) => {
      if (!projectId) {
        console.warn('[ChatService] 指令发送失败：当前未选中任何项目');
        return;
      }

      const { hidden = false } = options;

      // 1. 响应式更新 UI (立即回显用户消息，除非 hidden 为 true)
      if (!hidden) {
        appendMessage({
          id: `user-${nanoid()}`,
          role: 'user',
          content: command,
          timestamp: new Date(),
        });
      }

      // 2. 等待认证就绪
      const authenticated = await socketActions.waitForAuthenticated();
      if (!authenticated) {
        if (!hidden) {
          appendMessage({
            role: 'error',
            content: 'Authentication failed, please try again later',
            timestamp: new Date(),
          });
        }
        return;
      }

      // 3. 构造底层通信数据载荷 (符合文档 2.1)
      const payload = {
        type: 'input',
        project: projectId,
        content: command,
      };

      // 4. 发送消息
      socketActions.sendAgentMessage(payload, {
        projectId,
        sessionId,
      });

      // 5. 初始化生成相关的 UI 状态 (如果不是静默发送，则更新状态)
      if (!hidden) {
        rememberLegacyBuildStart(projectId, sessionId);
        setIsGenerating(true);
        setStatus({ text: 'Processing command...', tokens: 0, canInterrupt: true });
        setIsBuilding(true);
        setPendingCommand(command);
      }
    },
    [
      projectId,
      sessionId,
      appendMessage,
      setStatus,
      setIsGenerating,
      setIsBuilding,
      setPendingCommand,
    ],
  );

  /**
   * 中断当前正在运行的任务
   */
  const abortTask = useCallback(() => {
    if (!projectId || !sessionId) return;

    // 发送中断信号 (符合 agentMessage 结构)
    socketActions.sendAgentMessage(
      { type: 'abort', sessionId },
      { projectId: projectId, sessionId },
    );
  }, [projectId, sessionId]);

  /**
   * 清空对话
   */
  const clearChat = useCallback(() => {
    clearMessages();
  }, [clearMessages]);

  return {
    sendCommand,
    abortTask,
    clearChat,
  };
}
